'use client';

import { type ReactNode, createContext, useContext } from 'react';
import type { FlagEnvironment } from './admin-types.js';
import { hashString } from './hash.js';

export interface FlagDefinition {
	enabled: boolean;
	percentage?: number; // 0-100
	allowList?: string[]; // user IDs that always get the flag
	environments?: FlagEnvironment[];
}

export function evaluateFlag(
	flag: FlagDefinition | null,
	context: { userId?: string; visitorId: string; environment: FlagEnvironment },
): boolean {
	if (!flag || !flag.enabled) return false;

	// Environment gate
	if (flag.environments?.length && !flag.environments.includes(context.environment)) return false;

	// Allow-list check
	if (flag.allowList?.includes(context.userId ?? '')) return true;

	// Percentage rollout
	if (flag.percentage !== undefined) {
		const id = context.userId || context.visitorId;
		return hashString(id) % 100 < flag.percentage;
	}

	return flag.enabled;
}

const FlagContext = createContext<Record<string, boolean>>({});

export function FeatureFlagProvider({
	flags,
	children,
}: {
	flags: Record<string, boolean>;
	children: ReactNode;
}) {
	return <FlagContext.Provider value={flags}>{children}</FlagContext.Provider>;
}

export function useFeatureFlag(name: string): boolean {
	return useContext(FlagContext)[name] ?? false;
}
